/* Morgan & Reid — Free gift with Hot Chocolate (Ivory Snuggle Fleece Throw Rug).
 * STANDALONE module. Not the cart milestone, not the bundle widget.
 *
 * ARCHITECTURE (v2 — event-scoped, single-write, idempotent). The v1 module monkeypatched
 * window.fetch + XMLHttpRequest for EVERY /cart/(add|change|update) call and fired a reconcile
 * that raced the theme's own drawer render — which broke normal removes and bounced to /cart.
 * This version does none of that:
 *  - NO fetch/XHR patch. We listen ONLY to the theme's own post-render 'cart:updated' event
 *    (see cart.js: cart-items.onCartUpdate re-renders the drawer, THEN dispatches 'cart:updated').
 *    Because it fires after the DOM is settled, we never race the render.
 *  - reconcile() is a pure fixed-point: desired = (giftPerTrigger ? #HotChocolate : 1). If the
 *    gift line already equals `desired`, it writes NOTHING. So removing any unrelated product is
 *    a true no-op — the cart behaves exactly as if this script weren't loaded.
 *  - When a write IS needed it happens in ONE cart op (change.js on our line, or add.js when the
 *    line doesn't exist yet), then we ask the drawer to re-render via its native 'cart:refresh'
 *    event. Our raw /cart writes are invisible to the theme, so nothing collides.
 *  - Idempotent: run it twice on the same cart and the 2nd run is a no-op. Serialized on a promise
 *    chain so two passes never overlap. No requeue/pending/blockedAt bookkeeping.
 *
 * SAFETY / KILL ORDER:
 *  1. Read the master flag from /api/free-gift-config FIRST.
 *  2. active !== true (or gift not addable) -> remove any gift line we added, never add.
 *  3. After a write, re-check: if a gift line is NOT 100% discounted it's being charged -> REMOVE
 *     it and start a short cooldown, so a broken-discount state degrades to "throw removed, stays
 *     removed" for a while instead of flickering in and out. Never charge for a gift.
 * The gift line is tagged property _free_gift: 'true' so we only ever touch our own line.
 */
(function () {
  "use strict";

  var API_BASE = "https://commerce-command-79465365634.australia-southeast1.run.app";
  var SHOP = (window.Shopify && window.Shopify.shop) || "manchester-factory-australia.myshopify.com";
  var ROUTE_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  var GIFT_PROP = "_free_gift";
  var COOLDOWN_MS = 15000;          // after a safety-net removal, don't re-add for this long

  var scheduled = null;             // debounce timer
  var chain = Promise.resolve();    // serialize reconciles (no overlap; no requeue/pending state)
  var giftBlockedUntil = 0;         // epoch ms; while now() < this, do not (re-)add the gift

  function now() { return new Date().getTime(); }
  function j(url, opts) { return fetch(url, opts).then(function (r) { return r.ok ? r.json() : Promise.reject(r); }); }
  function getCart() { return j(ROUTE_ROOT + "cart.js"); }
  function getConfig() {
    return j(API_BASE + "/api/free-gift-config?shop=" + encodeURIComponent(SHOP)).catch(function () { return { active: false }; });
  }
  function num(v) { return String(v == null ? "" : v).replace(/\D/g, ""); }

  function giftLines(cart) {
    return cart.items.filter(function (it) {
      return it.properties && (it.properties[GIFT_PROP] === "true" || it.properties[GIFT_PROP] === true);
    });
  }
  // Set a specific line (by key) to an absolute quantity. 0 removes it. Optional `properties`
  // REPLACES the line's properties (used by gift-line adoption below).
  function changeLine(key, quantity, properties) {
    var body = { id: key, quantity: quantity };
    if (properties) body.properties = properties;
    return j(ROUTE_ROOT + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
  }
  function addGift(variantId, quantity) {
    return j(ROUTE_ROOT + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity, properties: { _free_gift: "true" } }] }),
    });
  }
  // Remove every gift line we own, one at a time (keys shift after each change; re-fetch between).
  function removeAllGift() {
    return getCart().then(function (c) {
      var g = giftLines(c)[0];
      if (!g) return;
      return changeLine(g.key, 0).then(removeAllGift);
    });
  }
  // Re-render the cart/drawer through the theme's NATIVE cartUpdate path (the one manual adds use),
  // NOT cart:refresh. cart:refresh -> onCartRefresh makes the Redo package-protection app stack a
  // duplicate toggle in the drawer footer on every one of our writes (Redo re-injects on cart:refresh
  // without removing the previous one). We fetch the FULL cart + rendered sections via a no-op
  // cart/update.js (item_count + sections — the exact shape the theme's own cartUpdate passes) and
  // publish that. Loop-safe: onCartUpdate re-dispatches 'cart:updated' -> our reconcile -> fixed
  // point (currentQty === desired) -> no-op. We never force the drawer open (no show()).
  function refreshDrawer() {
    var sections = [];
    try { document.documentElement.dispatchEvent(new CustomEvent("cart:bundled-sections", { bubbles: true, detail: { sections: sections } })); } catch (e) {}
    var pubsub = window.theme && window.theme.pubsub;
    return j(ROUTE_ROOT + "cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ sections: sections, sections_url: window.location.pathname }),
    }).then(function (cart) {
      if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.cartUpdate && cart && cart.sections) {
        pubsub.publish(pubsub.PUB_SUB_EVENTS.cartUpdate, { source: "free-gift", cart: cart });
      } else {
        try { document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: false } })); } catch (e) {}
      }
    }).catch(function () {});
  }

  // One idempotent pass. Writes at most once, then a post-write safety re-check. Returns a promise.
  function reconcileOnce() {
    return Promise.all([getConfig(), getCart()]).then(function (res) {
      var cfg = res[0], cart = res[1];
      var gifts = giftLines(cart);
      var currentQty = gifts.reduce(function (s, it) { return s + it.quantity; }, 0);

      // KILL ORDER: flag checked FIRST. Off / not addable => remove our line, never add.
      if (!cfg || cfg.active !== true || cfg.giftAddable !== true) {
        if (currentQty === 0) return false;
        return removeAllGift().then(function () { return true; });
      }

      // Trigger is now a LIST. Count qualifying units across ALL trigger products (falls back to the
      // singular triggerProductId for older config). One throw per qualifying trigger unit, summed.
      var triggerIds = (cfg.triggerProductIds && cfg.triggerProductIds.length ? cfg.triggerProductIds : [cfg.triggerProductId]).map(num);
      var giftVariantId = num(cfg.giftVariantId);
      var triggerQty = cart.items.reduce(function (s, it) {
        return triggerIds.indexOf(num(it.product_id)) !== -1 ? s + it.quantity : s;
      }, 0);
      var desired = triggerQty === 0 ? 0 : (cfg.giftPerTrigger ? triggerQty : 1);

      // ── STANDARD RULE for ALL M&R gift promos — GIFT-LINE ADOPTION ──
      // If a line is OUR gift product but doesn't carry OUR tag (fossil tags from dead promos, old
      // labels, manual adds) AND the customer is ENTITLED (desired > 0), ADOPT it: one idempotent
      // change.js rewrite to the clean current properties. From then on it counts in the fixed-point
      // math — no double-adds, no flicker, clean labels on orders. BOUNDARY: no entitlement -> no
      // adoption, NO TOUCH (the line stays as-is and rings full price); adoption claims lines that
      // should be gifts, it never makes anything free — only the Function does that. Lines owned by
      // ANOTHER live promo's tag (e.g. _mr_milestone_gift) are never adopted.
      if (desired > 0) {
        var adoptable = cart.items.filter(function (it) {
          if (num(it.variant_id) !== giftVariantId) return false;
          var p = it.properties || {};
          if (p[GIFT_PROP] === "true" || p[GIFT_PROP] === true) return false; // already ours
          if (p._mr_milestone_gift) return false; // owned by the milestone promo
          if (p._mr_cross_sell) return false; // owned by the cross-sell engine (customer chose to buy it)
          return true;
        });
        if (adoptable.length) {
          var props = {}; props[GIFT_PROP] = "true";
          // One adoption per pass; our own re-render event re-runs reconcile, which then counts the
          // adopted line normally (and trims quantity via the fixed point if needed).
          return changeLine(adoptable[0].key, adoptable[0].quantity, props).then(function () { return true; });
        }
      }

      // FIXED POINT: already correct => write nothing. This is what makes a non-gift change
      // (e.g. removing an unrelated product) a genuine no-op — no write, no refresh, no interference.
      if (currentQty === desired) return false;

      // Cooldown: a recent safety-net removal is in effect; don't re-add yet (removals still allowed).
      if (currentQty < desired && now() < giftBlockedUntil) return false;

      // --- Single write to reach `desired`. ---
      if (desired === 0) return removeAllGift().then(function () { return true; });
      if (gifts.length === 0) return addGift(giftVariantId, desired).then(function () { return true; });
      if (gifts.length === 1) return changeLine(gifts[0].key, desired).then(function () { return true; });
      // Rare: >1 gift line. Collapse to one at the exact quantity.
      return removeAllGift().then(function () { return addGift(giftVariantId, desired); }).then(function () { return true; });
    }).then(function (didWrite) {
      if (!didWrite) return;
      // Post-write safety: re-read; if a gift line is being charged, pull it and start a cooldown.
      return getCart().then(function (cart) {
        var charged = giftLines(cart).filter(function (it) { return it.final_line_price > 0; });
        if (charged.length) {
          giftBlockedUntil = now() + COOLDOWN_MS;   // degrade to "removed, stays removed" for a while
          return removeAllGift().then(refreshDrawer);
        }
        refreshDrawer();
      });
    }).catch(function (e) { if (window.console) console.log("[free-gift] reconcile error:", e); });
  }

  // Debounce bursts of cart events into one pass; append to the chain so passes never overlap.
  function schedule() {
    clearTimeout(scheduled);
    scheduled = setTimeout(function () {
      chain = chain.then(reconcileOnce).catch(function () {});
    }, 250);
  }

  // Wire ONLY the theme's own cart signals. No global fetch/XHR interception.
  function wire() {
    // Post-render DOM event the theme dispatches after every cart mutation (cart.js onCartUpdate).
    document.addEventListener("cart:updated", schedule);
    // The PDP Bundle Builder adds via /cart/add.js and signals ONLY with 'cart:refresh' (never
    // cart:updated / pubsub) — so we must listen for it too, or a Hot Chocolate added via a bundle
    // never triggers the gift. Safe to listen: our own refreshDrawer() also dispatches cart:refresh,
    // but the resulting reconcile hits the fixed point (currentQty === desired) and no-ops.
    document.addEventListener("cart:refresh", schedule);
    // Also the pubsub it publishes on every mutation — covers PDP add-to-cart, where no cart-items
    // element may be present to emit the DOM event. Both funnel to the same debounced, idempotent
    // pass, so double-firing is harmless.
    try {
      if (window.theme && theme.pubsub && theme.pubsub.PUB_SUB_EVENTS && theme.pubsub.PUB_SUB_EVENTS.cartUpdate) {
        theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () { schedule(); });
      }
    } catch (e) {}
  }

  function init() { wire(); schedule(); }   // schedule() covers landing with a Hot Chocolate already in cart
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
